import { useMutation, useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { FilterType } from '../../../@types/size';
import { timeKeepingApi, userApi } from '../../../apis';
import {
  Approver,
  CreateManyUserDto,
  CreateUserDataFromFile,
  CreateUserDto,
  UpdateUserDto,
  User,
  UserRoleEnum,
} from '../../../apis/client-axios';
import ButtonWrap from '../../../components/ButtonWrap';
import ImportCSV from '../../../components/ImportCSV/ImportCSV';
import ModalWrap from '../../../components/ModalWrap';
import SvgCsv from '../../../components/svg/SvgCsv';
import SvgCsvGreen from '../../../components/svg/SvgCsvGreen';
import SvgLockBlack from '../../../components/svg/SvgLockBlack';
import SvgLockRed from '../../../components/svg/SvgLockRed';
import SvgPencil from '../../../components/svg/SvgPencil';
import SvgPlus from '../../../components/svg/SvgPlus';
import TableWrap from '../../../components/TableWrap';
import ToastError from '../../../components/Toast/ToastError';
import ToastSuccess from '../../../components/Toast/ToastSuccess';
import Widget from '../../../components/Widget';
import { DATE_FORMAT, MONTH_YEAR_FORMAT, MONTH_YEAR_FORMAT_JP, YEAR_MONTH_FORMAT } from '../../../constants/common';
import { QUERY_KEY } from '../../../constants/QueryKey';
import { downloadCSV } from '../../../utils';
import FormStaff from './FormStaff/FormStaff';
import './style.scss';
import FormExportTimekeeping from './FormExportTimekeeping';
import SvgDelete from '../../../components/svg/SvgDelete';

interface OpenModalInformation {
  type: 'create' | 'update';
  data?: User;
}

interface IWarningModal {
  type: 'block' | 'delete';
  data?: User;
}

interface OpenModal {
  information?: OpenModalInformation;
  warning?: IWarningModal;
  csv: boolean;
  timekeeping: boolean;
}

const StaffManagement = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [formExportTimekeeping] = Form.useForm();
  const month = Form.useWatch('month', formExportTimekeeping);
  const [open, setOpen] = useState<OpenModal>({
    information: undefined,
    warning: undefined,
    csv: false,
    timekeeping: false,
  });
  const [dataImportCSV, setDataImportCSV] = useState<CreateUserDataFromFile[]>([]);
  const [filter, setFilter] = useState<FilterType>({
    page: 1,
    size: 20,
    sort: '',
    fullTextSearch: '',
  });
  const [dataCSVImportError, setDataCSVImportError] = useState<string>('');

  const columns = [
    {
      title: intl.formatMessage({ id: 'staff-management.table.employeeCode' }),
      ellipsis: true,
      dataIndex: 'employeeCode',
      key: 'employeeCode',
      width: 140,
    },
    {
      title: intl.formatMessage({ id: 'staff-management.table.name' }),
      ellipsis: true,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: intl.formatMessage({ id: 'staff-management.table.email' }),
      ellipsis: true,
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: intl.formatMessage({ id: 'staff-management.table.address' }),
      ellipsis: true,
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: intl.formatMessage({ id: 'staff-management.table.phoneNumber' }),
      ellipsis: true,
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 120,
    },
    {
      // width: 250,
      key: 'actions',
      render: (_, record) => {
        if (record.role === UserRoleEnum.SuperAdmin) return;
        return (
          <div className="staff-management__table-actions">
            {/* EDIT */}
            <div
              onClick={() => setOpen((prev) => ({ ...prev, information: { type: 'update', data: record } }))}
              className="staff-management__action-edit"
            >
              <SvgPencil /> {intl.formatMessage({ id: 'staff-management.table.edit' })}
            </div>

            {/* BLOCK / UNBLOCK */}
            {!record?.isActive ? (
              <div
                className="staff-management__action-status"
                onClick={() => setOpen((prev) => ({ ...prev, warning: { type: 'block', data: record } }))}
              >
                <SvgLockBlack /> {intl.formatMessage({ id: 'staff-management.table.unblock' })}
              </div>
            ) : (
              <div
                className="staff-management__action-status text-red"
                onClick={() => setOpen((prev) => ({ ...prev, warning: { type: 'block', data: record } }))}
              >
                <SvgLockRed /> {intl.formatMessage({ id: 'staff-management.table.block' })}
              </div>
            )}

            {/* DELETE */}
            <div
              onClick={() => setOpen((prev) => ({ ...prev, warning: { type: 'delete', data: record } }))}
              className="staff-management__action-edit text-red"
            >
              <SvgDelete /> {intl.formatMessage({ id: 'account.delete' })}
            </div>
          </div>
        );
      },
    },
  ];

  const {
    data: usersResponse,
    isLoading,
    refetch: refetchUserList,
  } = useQuery({
    queryKey: [QUERY_KEY.USER_LIST, filter],
    queryFn: () => userApi.userControllerGetListUsers(filter.page, filter.size, undefined, filter.fullTextSearch),
    enabled: !!filter,
  });

  const { data: userResponse } = useQuery({
    queryKey: [QUERY_KEY.USER_BY_ID, open.information?.data],
    queryFn: () => userApi.userControllerGetUserById(open.information?.data?.id || ''),
    enabled: !!open.information?.data,
  });

  const createUserMutation = useMutation({
    mutationFn: (userDto: CreateUserDto) => userApi.userControllerCreateUser(userDto),
    onSuccess: () => {
      refetchUserList();
      setOpen((prev) => ({ ...prev, information: undefined }));
      ToastSuccess({
        content: intl.formatMessage({ id: 'account-form.create.success' }),
      });
    },
    onError: (error: { response: { data: { message: string; data: string[] }; status: number } }) => {
      const { data, message } = error?.response?.data;

      if (Array.isArray(data) && data.length > 0) {
        if (message === 'USER_EXIST') {
          const message = data.map((item) => intl.formatMessage({ id: `staff-management.noti.${item}` }));

          return ToastError({
            content: `${message.join(', ')}${intl.formatMessage({ id: 'staff-management.noti.exist' })}`,
          });
        }
      }

      return ToastError({ content: intl.formatMessage({ id: 'staff-management.failed' }) });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: (userDto: { id: string } & UpdateUserDto) => userApi.userControllerUpdateUser(userDto.id, userDto),
    onSuccess: () => {
      refetchUserList();
      if (open.warning?.type === 'block') {
        setOpen((prev) => ({ ...prev, warning: undefined }));
        ToastSuccess({
          content: open.warning?.data?.isActive
            ? intl.formatMessage({ id: 'account-form.block.success' })
            : intl.formatMessage({ id: 'account-form.unblock.success' }),
        });
      } else {
        setOpen((prev) => ({ ...prev, information: undefined }));
        ToastSuccess({
          content: intl.formatMessage({ id: 'account-form.edit.success' }),
        });
      }
    },
    onError: (error: { response: { data: { message: string; data: string[] }; status: number } }) => {
      const { data, message } = error?.response?.data;

      if (Array.isArray(data) && data.length > 0) {
        if (message === 'USER_EXIST') {
          const message = data.map((item) => intl.formatMessage({ id: `staff-management.noti.${item}` }));

          return ToastError({
            content: `${message.join(', ')}${intl.formatMessage({ id: 'staff-management.noti.exist' })}`,
          });
        }
      }

      return ToastError({ content: intl.formatMessage({ id: 'staff-management.failed' }) });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userApi.userControllerDeleteUserById(userId),
    onSuccess: () => {
      refetchUserList();
      setOpen((prev) => ({ ...prev, warning: undefined }));
      ToastSuccess({
        content: intl.formatMessage({ id: 'account.delete.success' }),
      });
    },
    onError: (error) => {
      return ToastError({ content: intl.formatMessage({ id: 'account.delete.failed' }) });
    },
  });

  const exportUsersMutation = useMutation({
    mutationFn: () => userApi.userControllerExportUserToCsv(),
    onSuccess: (data) => {
      downloadCSV(data?.data?.data, `employees_${dayjs().format(DATE_FORMAT)}.csv`);
      ToastSuccess({
        content: intl.formatMessage({ id: 'common.csv.export.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'common.csv.export.failed' }),
      });
    },
  });

  const exportTimekeepingCsvMutation = useMutation({
    mutationFn: async () =>
      timeKeepingApi.timekeepingControllerExportTimekeepingToCsvAll(month.format(MONTH_YEAR_FORMAT), 1),
    onSuccess: (data: any) => {
      const filename = `勤怠確認_${dayjs(month).format(YEAR_MONTH_FORMAT)}.csv`;
      downloadCSV(data?.data?.data, filename);
      ToastSuccess({
        content: intl.formatMessage({ id: 'common.csv.export.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'common.csv.export.failed' }),
      });
    },
  });

  useEffect(() => {
    const userData = userResponse?.data?.data;

    if (userData) {
      form.setFieldsValue({
        ...userData,
        dateOfBirth: userData?.dateOfBirth ? dayjs(userData?.dateOfBirth) : undefined,
        officialWorkingDay: userData?.officialWorkingDay ? dayjs(userData?.officialWorkingDay) : undefined,
        employeeApprovals:
          Array.isArray(userData?.employeeApprovals) && userData?.employeeApprovals.length > 0
            ? userData?.employeeApprovals.sort((a, b) => a.level - b.level)
            : [{}],
      });
    }
  }, [userResponse?.data?.data]);

  const handleSubmit = () => {
    form
      .validateFields({ validateOnly: true })
      .then(() => {
        const data = form.getFieldsValue();

        const approvals =
          form.getFieldValue('employeeApprovals')?.filter((approve) => approve && approve?.managerId) || [];

        const employeeApprovals: Approver[] = [];

        approvals.forEach((approve, index) => {
          employeeApprovals.push({ ...approve, level: index + 1 });
        });

        if (open.information?.data) {
          updateUserMutation.mutate({
            ...data,
            password: data?.displayPassword,
            employeeApprovals,
            id: open.information?.data?.id,
            annualLeaveDaysRemaining: data?.annualLeaveDaysRemaining || 0,
            compensatoryLeaveDaysRemaining: data?.compensatoryLeaveDaysRemaining || 0,
          });
        } else {
          createUserMutation.mutate({
            ...data,
            password: data?.displayPassword,
            employeeApprovals,
            annualLeaveDaysRemaining: data?.annualLeaveDaysRemaining || 0,
            compensatoryLeaveDaysRemaining: data?.compensatoryLeaveDaysRemaining || 0,
          });
        }
      })
      .catch((error) => {
        form.validateFields();
      });
  };

  const handleImportCSV = (e: unknown[]) => {
    setDataImportCSV(e as unknown[] as CreateUserDataFromFile[]);
    setDataCSVImportError('');
  };

  const handleUpdateUserStatus = () => {
    updateUserMutation.mutate({
      ...open.warning?.data,
      isActive: !open.warning?.data?.isActive,
      id: open.warning?.data?.id || '',
    });
  };

  const handleDeleteUser = () => {
    if (open.warning?.data?.id) deleteUserMutation.mutate(open.warning?.data?.id);
  };

  const handleSubmitImportCSV = () => {
    isEmpty(dataImportCSV)
      ? ToastError({
          content: intl.formatMessage({ id: 'staff-management.import.empty' }),
        })
      : createManyUserMutation.mutate({ data: dataImportCSV });
  };

  const createManyUserMutation = useMutation({
    mutationFn: (dto: CreateManyUserDto) => userApi.userControllerCreateManyUser(dto),
    onSuccess: () => {
      handleCloseModalImportCSV();
      ToastSuccess({
        content: intl.formatMessage({ id: 'account-form.create.success' }),
      });
    },
    onError: (error: { response: { data: { message: string; data: string[] }; status: number } }) => {
      const { data, message } = error?.response?.data;

      if (message === 'IMPORT_FAILED') {
        if (typeof data === 'string') {
          setDataCSVImportError(data);
        }

        return ToastError({
          content: intl.formatMessage({ id: 'staff-management.failed.csvInvalidData' }),
        });
      }

      return ToastError({
        content: intl.formatMessage({ id: 'staff-management.failed' }),
      });
    },
    onSettled: () => {
      refetchUserList();
    },
  });

  const handleExportCSV = () => {
    exportUsersMutation.mutate();
  };

  const handleConvertDataFile = (fileData: unknown[], keyMapping?: Object) => {
    let result: CreateUserDataFromFile[] = [];
    fileData.forEach((item, index) => {
      const user = {
        employeeApprovals: [] as unknown[],
      };

      for (const key in item as Record<string | number, unknown>) {
        const attr = keyMapping?.[key] || key;
        user[attr] = item?.[key];

        if (attr === 'approver1') {
          user.employeeApprovals.push({
            level: 1,
            employeeCode: item?.[key] || '',
          });
        } else if (attr === 'approver2') {
          user.employeeApprovals.push({
            level: 2,
            employeeCode: item?.[key] || '',
          });
        }
      }

      if (Object.values(user).some((value) => (Array.isArray(value) ? value.length : value)))
        result.push(user as CreateUserDataFromFile);
    });

    // dùng để đổi lại level duyệt cho quản lý
    result = result.map((item) => {
      if (Array.isArray(item.employeeApprovals) && item.employeeApprovals.length > 0) {
        if (item.employeeApprovals.length === 1) {
          if (item.employeeApprovals?.[0]?.level === 2) {
            return {
              ...item,
              approver1: item.approver2,
              approver2: '',
              employeeApprovals: [{ ...item.employeeApprovals?.[0], level: 1 }],
            };
          }

          return item;
        } else if (item.employeeApprovals.length === 2) {
          const employeeApprovals = item.employeeApprovals;

          const level1Index = employeeApprovals?.findIndex((data: any) => data.level === 1);
          const level2Index = employeeApprovals?.findIndex((data: any) => data.level === 2);

          if (level1Index > -1 && level2Index > -1 && !employeeApprovals?.[level1Index]?.employeeCode) {
            employeeApprovals[level1Index].level = 2;
            employeeApprovals[level2Index].level = 1;

            const approver1 = employeeApprovals[level2Index].employeeCode;
            const approver2 = employeeApprovals[level1Index].employeeCode;

            return {
              ...item,
              approver1,
              approver2,
              employeeApprovals: employeeApprovals.sort((a, b) => a.level - b.level),
            };
          }

          return item;
        }

        return item;
      }

      return item;
    });

    return result;
  };

  const handleCloseModalImportCSV = () => {
    setOpen((prev) => ({ ...prev, csv: false }));
    setDataCSVImportError('');
    setDataImportCSV([]);
  };

  return (
    <div className="staff-management">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'staff-management.title' })]}
        content={
          <TableWrap
            isPagination
            loading={isLoading}
            onSearch={(keyword) => setFilter((prev) => ({ ...prev, fullTextSearch: keyword, page: 1 }))}
            paginationProps={{
              current: filter.page,
              pageSize: filter.size,
              total: usersResponse?.data?.data?.total || 0,
              onChange: (page) => setFilter((prev) => ({ ...prev, page })),
            }}
            tableProps={{
              dataSource: Array.isArray(usersResponse?.data?.data?.content) ? usersResponse?.data?.data?.content : [],
              columns,
            }}
            inputProps={{
              placeholder: intl.formatMessage({ id: 'staff-management.search.placeholder' }),
              width: 450,
            }}
            rightTopContent={
              <div className="staff-management__actions">
                <ButtonWrap
                  variant="outlined"
                  color="green"
                  onClick={() => setOpen((prev) => ({ ...prev, timekeeping: true }))}
                >
                  <SvgCsvGreen /> {intl.formatMessage({ id: 'account.timekeepingStatistics.export.title' })}
                </ButtonWrap>
                <ButtonWrap variant="outlined" color="green" onClick={handleExportCSV}>
                  <SvgCsvGreen /> {intl.formatMessage({ id: 'account.information.export.title' })}
                </ButtonWrap>
                <ButtonWrap variant="outlined" onClick={() => setOpen((prev) => ({ ...prev, csv: true }))}>
                  <SvgCsv /> {intl.formatMessage({ id: 'account.information.import.title' })}
                </ButtonWrap>
                <ButtonWrap
                  variant="filled"
                  color="primary"
                  width={124}
                  onClick={() => setOpen((prev) => ({ ...prev, information: { type: 'create' } }))}
                >
                  <SvgPlus />
                  {intl.formatMessage({ id: 'staff-management.btnAdd' })}
                </ButtonWrap>
              </div>
            }
          />
        }
        title={intl.formatMessage({ id: 'staff-management.title' })}
      />

      {open.information && (
        <ModalWrap
          open={!!open.information}
          loading={false}
          cancelText={intl.formatMessage({ id: 'common.cancel' })}
          submitText={
            open.information?.data
              ? intl.formatMessage({ id: 'common.save' })
              : intl.formatMessage({ id: 'staff-management.btnAdd' })
          }
          onSubmit={handleSubmit}
          onCancel={() => setOpen((prev) => ({ ...prev, information: undefined }))}
          buttonSubmitProps={{ htmlType: 'submit' }}
          type="upsert"
          title={
            open.information?.data
              ? intl.formatMessage({ id: 'staff-management.modal.edit' })
              : intl.formatMessage({ id: 'staff-management.modal.add' })
          }
          isScrollBody={true}
          body={
            <Form layout="vertical" form={form} name="form_in_modal" clearOnDestroy>
              <FormStaff form={form} />
            </Form>
          }
        />
      )}

      {open.warning && (
        <ModalWrap
          open={!!open.warning}
          cancelText={intl.formatMessage({ id: 'common.cancel' })}
          submitText={
            open.warning?.type === 'delete'
              ? intl.formatMessage({ id: 'common.delete' })
              : open.warning?.data?.isActive
                ? intl.formatMessage({ id: 'staff-management.table.block' })
                : intl.formatMessage({ id: 'staff-management.submit.unblock' })
          }
          onSubmit={open.warning?.type === 'delete' ? handleDeleteUser : handleUpdateUserStatus}
          onCancel={() => setOpen((prev) => ({ ...prev, warning: undefined }))}
          type={open.warning?.type === 'delete' || open.warning?.data?.isActive ? 'delete' : 'upsert'}
          title={
            open.warning?.type === 'delete'
              ? intl.formatMessage({ id: 'account.delete.title' })
              : open.warning?.data?.isActive
                ? intl.formatMessage({ id: 'staff-management.modal.block.title' })
                : intl.formatMessage({ id: 'staff-management.modal.unblock.title' })
          }
          body={
            <div className="modal-block-user">
              <span>{open.warning?.data?.name}</span>
              {open.warning?.type === 'delete' ? (
                <span>{intl.formatMessage({ id: 'account.delete.confirm' })}</span>
              ) : open.warning?.data?.isActive ? (
                <span>{intl.formatMessage({ id: 'staff-management.modal.block.desc' })}</span>
              ) : (
                <span>{intl.formatMessage({ id: 'staff-management.modal.unblock.desc' })}</span>
              )}
            </div>
          }
        />
      )}

      {open.csv && (
        <ModalWrap
          open={open.csv}
          cancelText={intl.formatMessage({ id: 'common.cancel' })}
          submitText={intl.formatMessage({ id: 'common.submit' })}
          buttonSubmitProps={{ loading: createManyUserMutation.isPending }}
          onSubmit={handleSubmitImportCSV}
          onCancel={handleCloseModalImportCSV}
          type="upsert"
          title={intl.formatMessage({ id: 'common.csv.import.title' })}
          isScrollBody
          body={
            <ImportCSV
              onUploadFile={handleImportCSV}
              type="USER"
              onConvertDataFile={handleConvertDataFile}
              dataCSVImportError={dataCSVImportError}
            />
          }
          width={1200}
        />
      )}

      {open.timekeeping && (
        <ModalWrap
          open={open.timekeeping}
          buttonSubmitProps={{ loading: exportTimekeepingCsvMutation.isPending }}
          onSubmit={() => exportTimekeepingCsvMutation.mutate()}
          onCancel={() => setOpen((prev) => ({ ...prev, timekeeping: false }))}
          type="upsert"
          title={intl.formatMessage({ id: 'account.timekeepingStatistics.export.title' })}
          body={<FormExportTimekeeping form={formExportTimekeeping} />}
          submitText={intl.formatMessage({ id: 'account.timekeepingStatistics.export.modal.submit' })}
        />
      )}
    </div>
  );
};

export default StaffManagement;
